import { cva, type VariantProps } from "class-variance-authority";
import { AlertCircle, CheckCircle2, Info, Loader2 } from "lucide-react";
import type { HTMLAttributes } from "react";

import { cn } from "../cn";

const alertVariants = cva("flex gap-3 rounded-md border p-3 text-sm", {
  variants: {
    tone: {
      info: "border-border bg-muted text-foreground",
      success: "border-mint-500/40 bg-mint-500/10 text-foreground",
      error: "border-destructive/40 bg-destructive/10 text-foreground",
    },
  },
  defaultVariants: { tone: "info" },
});

const icons = { info: Info, success: CheckCircle2, error: AlertCircle } as const;

export function Alert({ tone, className, children, ...props }: HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>) {
  const Icon = icons[tone ?? "info"];
  return (
    <div role={tone === "error" ? "alert" : "status"} className={cn(alertVariants({ tone }), className)} {...props}>
      <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <div>{children}</div>
    </div>
  );
}

const badgeVariants = cva("inline-flex items-center rounded-[var(--radius-pill)] px-2.5 py-0.5 text-xs font-medium", {
  variants: {
    tone: {
      neutral: "bg-muted text-muted-foreground",
      success: "bg-mint-500/15 text-mint-700 dark:text-mint-400",
      warning: "bg-coral-500/15 text-coral-700 dark:text-coral-400",
      accent: "bg-accent text-accent-foreground",
    },
  },
  defaultVariants: { tone: "neutral" },
});

export function Badge({ tone, className, ...props }: HTMLAttributes<HTMLSpanElement> & VariantProps<typeof badgeVariants>) {
  return <span className={cn(badgeVariants({ tone }), className)} {...props} />;
}

export function Spinner({ label, className }: { label: string; className?: string }) {
  return (
    <span role="status" className={cn("inline-flex items-center gap-2 text-sm text-muted-foreground", className)}>
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
      <span>{label}</span>
    </span>
  );
}

/** Determinate progress bar (0–100). */
export function Progress({ value, label, className }: { value: number; label: string; className?: string }) {
  const clamped = Math.max(0, Math.min(100, Math.round(value)));
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={clamped}
      className={cn("h-2 w-full overflow-hidden rounded-[var(--radius-pill)] bg-muted", className)}
    >
      <div className="h-full rounded-[var(--radius-pill)] bg-accent transition-[width] duration-700 ease-[var(--ease-out-expo)]" style={{ width: `${clamped}%` }} />
    </div>
  );
}

export function Avatar({ name, className }: { name: string; className?: string }) {
  const initials = name
    .split(/\s+/)
    .filter((p) => p.length > 0 && !p.endsWith("."))
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");
  return (
    <span aria-hidden className={cn("inline-flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-azure-400 to-azure-700 text-sm font-semibold text-white", className)}>
      {initials || "?"}
    </span>
  );
}

export function EmptyState({ title, description, icon, className }: { title: string; description: string; icon?: React.ReactNode; className?: string }) {
  return (
    <div className={cn("flex flex-col items-center gap-2 rounded-[var(--radius-lg)] border border-dashed border-border p-10 text-center", className)}>
      {icon}
      <h3 className="font-semibold">{title}</h3>
      <p className="max-w-md text-sm text-muted-foreground">{description}</p>
    </div>
  );
}
